import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  AutomationExecutionListModel,
  AutomationExecutionModel,
  AutomationExecutionStatus,
  AutomationDelayConfig,
  AutomationPlaceholderItem,
  AutomationPlaceholdersResponse,
  CreateWorkflowAutomationRequestModel,
  WhatsAppTemplateModel,
  WhatsAppTemplateVariableSlotModel,
  ToggleWorkflowAutomationRequestModel,
  UpdateWorkflowAutomationRequestModel,
  WorkflowAutomationConditionModel,
  WorkflowAutomationConditionField,
  WorkflowAutomationConditionOperator,
  WorkflowAutomationListModel,
  WorkflowAutomationMetadata,
  WorkflowAutomationModel,
} from '../models/automation-api.models';
import { asArray, asBoolean, asRecord, asString } from './automation-api.utils';
import { normalizeWorkflowAutomationActionTypeFromApi } from './automation-action-type.util';
import { normalizeWorkflowAutomationTriggerTypeFromApi } from './automation-trigger-type.util';
import {
  normalizeActionConfigFromApi,
  parseDelayConfigFromApi,
  parseTriggerConfigForUi,
  toWireAutomationBody,
} from './automation-wire-payload.util';

const CONDITION_FIELD_FROM_INT: Record<number, WorkflowAutomationConditionField> = {
  1: 'CardField', 2: 'CardStage', 3: 'ChecklistCompleted', 4: 'CardComments',
};

const CONDITION_OP_FROM_INT: Record<number, WorkflowAutomationConditionOperator> = {
  1: 'Equals', 2: 'NotEquals', 3: 'GreaterThan', 4: 'LessThan', 5: 'Contains',
  6: 'GreaterThanOrEqual', 7: 'LessThanOrEqual', 8: 'NotContains',
  9: 'IsEmpty', 10: 'IsNotEmpty', 11: 'IsTrue', 12: 'IsFalse',
  20: 'ChangedTo', 21: 'ChangedFromTo', 22: 'BecameFilled', 23: 'BecameEmpty',
  30: 'DateIsToday', 31: 'DateIsOverdue', 32: 'DateIsWithinDays',
};

const EXEC_STATUS_FROM_INT: Record<number, AutomationExecutionStatus> = {
  1: 'Success', 2: 'ConditionNotMet', 3: 'ActionFailed', 4: 'Skipped', 5: 'Aborted', 6: 'Scheduled',
};

const CONDITION_FIELD_FROM_STRING: Record<string, WorkflowAutomationConditionField> = {
  cardfield: 'CardField',
  cardstage: 'CardStage',
  checklistcompleted: 'ChecklistCompleted', cardcomments: 'CardComments',
};

function normalizeConditionField(raw: unknown): WorkflowAutomationConditionField {
  if (typeof raw === 'number') {
    return CONDITION_FIELD_FROM_INT[raw] ?? 'CardField';
  }
  const value = asString(raw).trim();
  if (value === 'CardField' || value === 'CardStage' || value === 'ChecklistCompleted' || value === 'CardComments') {
    return value;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return CONDITION_FIELD_FROM_INT[numeric] ?? 'CardField';
  }
  return CONDITION_FIELD_FROM_STRING[value.toLowerCase()] ?? 'CardField';
}

const CONDITION_OP_FROM_STRING: Record<string, WorkflowAutomationConditionOperator> = Object.fromEntries(
  Object.keys(CONDITION_OP_FROM_INT).flatMap(k => {
    const val = CONDITION_OP_FROM_INT[Number(k) as keyof typeof CONDITION_OP_FROM_INT];
    return val ? [[val.toLowerCase(), val]] : [];
  })
);

function normalizeConditionOperator(raw: unknown): WorkflowAutomationConditionOperator {
  if (typeof raw === 'number') {
    return CONDITION_OP_FROM_INT[raw] ?? 'Equals';
  }
  const value = asString(raw).trim();
  if (!value) return 'Equals';
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return CONDITION_OP_FROM_INT[numeric] ?? 'Equals';
  }
  return CONDITION_OP_FROM_STRING[value.toLowerCase()] ?? 'Equals';
}

function parseConditionValue(
  field: WorkflowAutomationConditionField,
  rawValue: unknown
): unknown {
  if (field === 'ChecklistCompleted') {
    if (typeof rawValue === 'boolean') return rawValue;
    const normalized = String(rawValue ?? '').trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
    return rawValue;
  }

  if (field !== 'CardField' && field !== 'CardComments') {
    return rawValue;
  }

  if (rawValue && typeof rawValue === 'object') {
    return rawValue;
  }

  if (typeof rawValue !== 'string') {
    return rawValue;
  }

  const trimmed = rawValue.trim();
  if (!trimmed) return { workflowFieldId: '', value: '' };

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {}

  return rawValue;
}

@Injectable({ providedIn: 'root' })
export class WorkflowAutomationsApiService {
  private readonly http = inject(HttpClient);

  async listByWorkflow(workflowId: string): Promise<WorkflowAutomationListModel> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflows/${workflowId}/automations`)
    );
    return {
      items: asArray(response).map((entry) => this.mapAutomation(entry, workflowId)),
    };
  }

  async getById(workflowId: string, automationId: string): Promise<WorkflowAutomationModel | null> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflows/${workflowId}/automations/${automationId}`)
    );
    const mapped = this.mapAutomation(response, workflowId);
    return mapped.id ? mapped : null;
  }

  async create(
    workflowId: string,
    payload: CreateWorkflowAutomationRequestModel
  ): Promise<WorkflowAutomationModel> {
    const body = toWireAutomationBody(payload);
    const response = await firstValueFrom(
      this.http.post<unknown>(`/api/workflows/${workflowId}/automations`, body)
    );
    return this.mapAutomation(response, workflowId);
  }

  async update(
    workflowId: string,
    automationId: string,
    payload: UpdateWorkflowAutomationRequestModel
  ): Promise<WorkflowAutomationModel> {
    const body = toWireAutomationBody(payload);
    (body as unknown as Record<string, unknown>)['id'] = automationId;
    (body as unknown as Record<string, unknown>)['workflowId'] = workflowId;
    const response = await firstValueFrom(
      this.http.put<unknown>(`/api/workflows/${workflowId}/automations/${automationId}`, body)
    );
    return this.mapAutomation(response, workflowId);
  }

  async toggle(
    workflowId: string,
    automationId: string,
    payload: ToggleWorkflowAutomationRequestModel
  ): Promise<WorkflowAutomationModel | null> {
    const response = await firstValueFrom(
      this.http.patch<unknown>(`/api/workflows/${workflowId}/automations/${automationId}/toggle`, {
        id: automationId,
        workflowId,
        isActive: payload.isActive,
      })
    );
    const mapped = this.mapAutomation(response, workflowId);
    return mapped.id ? mapped : null;
  }

  async delete(workflowId: string, automationId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/workflows/${workflowId}/automations/${automationId}`));
  }

  async getExecutionLogs(
    workflowId: string,
    automationId: string,
    limit = 20,
    offset = 0
  ): Promise<AutomationExecutionListModel> {
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(
          `/api/workflows/${workflowId}/automations/${automationId}/execution-logs`,
          { params: { limit: limit.toString(), offset: offset.toString() } }
        )
      );
      return this.mapExecutionList(response);
    } catch {
      return { items: [], totalCount: 0 };
    }
  }

  async getPlaceholders(workflowId: string): Promise<AutomationPlaceholdersResponse> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflows/${workflowId}/automations/placeholders`)
    );
    return this.mapPlaceholdersResponse(response);
  }

  async getWhatsAppTemplates(): Promise<WhatsAppTemplateModel[]> {
    const response = await this.getWithFallback(['/api/whatsapp/templates', '/whatsapp/templates']);
    return asArray(response)
      .map((entry) => this.mapWhatsAppTemplate(entry))
      .filter((item) => !!item.key);
  }

  private asPositiveInt(value: unknown, fallback: number): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
  }

  private mapAutomation(raw: unknown, workflowId: string): WorkflowAutomationModel {
    const source = asRecord(raw);
    const wid = asString(source['workflowId'] ?? source['WorkflowId']) || workflowId;
    const triggerType = normalizeWorkflowAutomationTriggerTypeFromApi(
      source['triggerType'] ?? source['TriggerType']
    );
    const rawConditions = asArray(source['conditions'] ?? source['Conditions']);
    let triggerConfigJson = this.parseConfigRecord(
      source['triggerConfigJson']
      ?? source['TriggerConfigJson']
      ?? source['triggerConfig']
      ?? source['TriggerConfig']
    );
    triggerConfigJson = parseTriggerConfigForUi(triggerType, triggerConfigJson, rawConditions);

    const actions = asArray(source['actions'] ?? source['Actions']);
    let actionType = normalizeWorkflowAutomationActionTypeFromApi(
      source['actionType'] ?? source['ActionType']
    );
    let actionConfigJson: Record<string, unknown> | null = null;

    if (actions.length > 0) {
      const a0 = asRecord(actions[0]);
      actionType = normalizeWorkflowAutomationActionTypeFromApi(a0['type'] ?? a0['Type']);
      actionConfigJson = this.parseConfigRecord(
        a0['configJson'] ?? a0['ConfigJson'] ?? a0['config'] ?? a0['Config']
      );
    } else {
      actionConfigJson = this.parseConfigRecord(
        source['actionConfigJson']
        ?? source['ActionConfigJson']
        ?? source['actionConfig']
        ?? source['ActionConfig']
      );
    }

    if (actionConfigJson) {
      const normalized = normalizeActionConfigFromApi(actionType, actionConfigJson);
      if (normalized) actionConfigJson = normalized;
    }

    const automationId = asString(source['id'] ?? source['Id']);
    const conditions = rawConditions.map((c) => this.mapCondition(c, automationId));

    return {
      id: automationId,
      workflowId: wid,
      name: asString(source['name'] ?? source['Name']),
      description: asString(source['description'] ?? source['Description']) || undefined,
      triggerType,
      actionType,
      actionConfigJson,
      triggerConfigJson,
      delayConfig: parseDelayConfigFromApi(source),
      isActive: asBoolean(source['isActive'] ?? source['IsActive']),
      createdAt: asString(source['createdAt'] ?? source['CreatedAt']) || undefined,
      updatedAt: asString(source['updatedAt'] ?? source['UpdatedAt']) || undefined,
      conditions,
      metadata: this.parseMetadata(source['metadata'] ?? source['Metadata']),
    };
  }

  private mapCondition(raw: unknown, automationId = ''): WorkflowAutomationConditionModel {
    const source = asRecord(raw);
    const field = normalizeConditionField(source['field'] ?? source['Field']);
    return {
      id: asString(source['id'] ?? source['Id']),
      workflowAutomationId:
        asString(source['workflowAutomationId'] ?? source['WorkflowAutomationId']) || automationId,
      field,
      operator: normalizeConditionOperator(source['operator'] ?? source['Operator']),
      value: parseConditionValue(field, source['value'] ?? source['Value'] ?? ''),
    };
  }

  private parseMetadata(raw: unknown): WorkflowAutomationMetadata | null {
    if (!raw || typeof raw !== 'object') return null;
    const s = raw as Record<string, unknown>;
    return {
      supportsConditions: s['supportsConditions'] === true,
      supportsSingleAction: s['supportsSingleAction'] === true,
      supportedConditionOperators: asArray(s['supportedConditionOperators']).filter(
        (v): v is number => typeof v === 'number'
      ),
      supportedCardLookupSafetyModes: asArray(s['supportedCardLookupSafetyModes']).filter(
        (v): v is number => typeof v === 'number'
      ),
      supportedDelayModes: asArray(s['supportedDelayModes']).filter(
        (v): v is number => typeof v === 'number'
      ),
      supportedDelayUnits: asArray(s['supportedDelayUnits']).filter(
        (v): v is number => typeof v === 'number'
      ),
    };
  }

  private mapExecutionList(raw: unknown): AutomationExecutionListModel {
    const source = asRecord(raw);
    const items = asArray(source['items']).map((e) => this.mapExecution(e));
    return {
      items,
      totalCount: this.asPositiveInt(source['totalCount'] ?? source['total'], items.length),
    };
  }

  private mapExecution(raw: unknown): AutomationExecutionModel {
    const source = asRecord(raw);

    const statusRaw = source['status'];
    let status: AutomationExecutionStatus;
    if (typeof statusRaw === 'number') {
      status = EXEC_STATUS_FROM_INT[statusRaw] ?? 'ActionFailed';
    } else {
      const s = asString(statusRaw);
      const valid: AutomationExecutionStatus[] = [
        'Success', 'ConditionNotMet', 'ActionFailed', 'Skipped', 'Aborted', 'Scheduled',
      ];
      status = valid.includes(s as AutomationExecutionStatus) ? s as AutomationExecutionStatus : 'ActionFailed';
    }

    return {
      id: asString(source['id']),
      workflowAutomationId: asString(source['workflowAutomationId']),
      cardId: asString(source['cardId']),
      targetCardId: asString(source['targetCardId']) || null,
      triggerType: normalizeWorkflowAutomationTriggerTypeFromApi(source['triggerType']),
      actionType: normalizeWorkflowAutomationActionTypeFromApi(source['actionType']),
      status,
      conditionsEvaluatedJson: asString(source['conditionsEvaluatedJson']) || null,
      actionDetailJson: asString(source['actionDetailJson']) || null,
      failureReason: asString(source['failureReason']) || null,
      executedAt: asString(source['executedAt']),
    };
  }

  private parseConfigRecord(raw: unknown): Record<string, unknown> | null {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        return null;
      }
    }
    return null;
  }

  private mapWhatsAppTemplate(raw: unknown): WhatsAppTemplateModel {
    const source = asRecord(raw);
    const id =
      asString(source['id']).trim()
      || asString(source['key']).trim()
      || asString(source['templateKey']).trim();
    const slotsRaw = asArray(source['variableSlots']);
    const variableSlots: WhatsAppTemplateVariableSlotModel[] = slotsRaw
      .map((row, idx) => {
        const rec = asRecord(row);
        const key = asString(rec['key']).trim();
        const label = asString(rec['label']).trim();
        const index = this.asPositiveInt(rec['index'], idx + 1);
        if (!key) return null;
        return { index: index || idx + 1, key, label: label || key };
      })
      .filter((s): s is WhatsAppTemplateVariableSlotModel => s !== null);
    const variables = asArray(source['variables'])
      .map((item) => asString(item).trim())
      .filter((item) => item.length > 0);
    const placeholdersLegacy = asArray(source['placeholders'])
      .map((item) => asString(item).trim())
      .filter((item) => item.length > 0);
    let slotsOut = variableSlots;
    if (!slotsOut.length && variables.length > 0) {
      slotsOut = variables.map((key, i) => ({ index: i + 1, key, label: key }));
    }
    const orderedKeysFromSlots = [...slotsOut].sort((a, b) => a.index - b.index).map((s) => s.key);
    const placeholders =
      orderedKeysFromSlots.length > 0
        ? orderedKeysFromSlots
        : variables.length > 0
          ? variables
          : placeholdersLegacy;
    const variablesOut = variables.length > 0 ? variables : placeholders;
    return {
      key: id,
      name: asString(source['name']).trim() || asString(source['title']).trim() || id || '—',
      description: asString(source['description']).trim() || undefined,
      language: asString(source['language']).trim() || undefined,
      category: asString(source['category']).trim() || undefined,
      status: asString(source['status']).trim() || undefined,
      bodyPreview: asString(source['bodyPreview']).trim() || undefined,
      variables: variablesOut,
      variableSlots: slotsOut,
      placeholders,
    };
  }

  private mapPlaceholdersResponse(raw: unknown): AutomationPlaceholdersResponse {
    const source = asRecord(raw);
    const mapItems = (arr: unknown[]): AutomationPlaceholderItem[] =>
      arr.map((entry) => {
        const row = asRecord(entry);
        return {
          placeholder: asString(row['placeholder']),
          label: asString(row['label']),
          fieldType: asString(row['fieldType']) || 'text',
        };
      }).filter((item) => !!item.placeholder);
    return {
      standardPlaceholders: mapItems(asArray(source['standardPlaceholders'])),
      customFieldPlaceholders: mapItems(asArray(source['customFieldPlaceholders'])),
    };
  }

  private async getWithFallback(endpoints: string[]): Promise<unknown> {
    let lastError: unknown;
    for (const endpoint of endpoints) {
      try {
        return await firstValueFrom(this.http.get<unknown>(endpoint));
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError ?? new Error('Request failed');
  }
}
